/**
 * "What would the money have to look like?" — the one follow-up under the
 * priority ladder that is a pair of numbers rather than a pile of chips.
 *
 * Two thumbs on one track: the floor they would seriously consider, and the
 * realistic top they are hopeful for. Both are needed. A floor alone reads as
 * a demand and a target alone reads as a wish, and a coach can act on neither
 * without the other beside it.
 *
 * Every rule and every sentence lives here rather than in the component, for
 * the same reason as the ladder and the piles: the copy is the feature, and
 * "under $50k is below where almost every engineering offer starts" is the
 * whole point of drawing this as a range instead of asking for one number.
 */

/** What the applicant put the two thumbs on. Whole dollars, a year. */
export interface CompRange {
  /** The least they would seriously consider. */
  min: number
  /** The realistic top they are hopeful for. */
  max: number
}

/**
 * The grid the thumbs land on.
 *
 * $25k rather than $1k because the question is which bracket they are in, not
 * what they would sign for. A slider that can stop on $237,000 invites an
 * answer more precise than anybody's real expectation, and a reviewer reading
 * it would take the precision seriously.
 */
export const COMP_STEP = 25_000

/**
 * The end of the track.
 *
 * A million is past the top of the market for all but a handful of people,
 * which is the point: a ceiling somebody can actually reach is a ceiling that
 * makes their answer look like ours.
 */
export const COMP_MAX = 1_000_000

/** Under this we say so — it is below where engineering offers start. */
export const COMP_LOW = 50_000

/** Past this we say so — it is senior staff and above, at a few companies. */
export const COMP_HIGH = 600_000

/**
 * Where the thumbs sit before anybody touches them.
 *
 * Deliberately NOT an answer, the same fence `startingPriorities` puts around
 * the handed-over ladder: nothing is stored until a thumb moves, so a range
 * the applicant was shown and a range they gave are never the same row in the
 * admin recap. The widget draws this when the bag holds nothing.
 */
export const COMP_START: Readonly<CompRange> = {
  min: 150_000,
  max: 300_000,
}

/** The sentences the component draws around the track. */
export const COMP_COPY = {
  /** What each thumb is called, to a screen reader and in the tips below. */
  minLabel: 'Minimum total compensation',
  maxLabel: 'Realistic top of the range',
  /** The control that opens the two explanations. */
  explain: 'What these two numbers mean',
  minTip:
    'The floor is the minimum compensation you would seriously consider accepting.',
  maxTip:
    'There probably is not a maximum salary you would accept, but the top number is the most you are hopeful for while still being realistic and not overly optimistic.',
  /**
   * Said in place of nothing, and never hidden behind a tap.
   *
   * These two are the reason the question is a range at all. A floor below
   * where offers start and a ceiling above where they land are both worth a
   * second look, and an applicant who only finds that out in a coaching call
   * has already spent the answer.
   */
  lowFlag:
    'Under $50k is below where almost every engineering offer starts, so we will ask whether you meant it.',
  highFlag:
    'Past $600k is senior staff and above at a handful of companies. Keep it here only if that is genuinely in reach.',
} as const

/** A number put on the grid, and inside the track. */
export function snapComp(value: number): number {
  if (!Number.isFinite(value)) return 0
  const stepped = Math.round(value / COMP_STEP) * COMP_STEP
  return Math.max(0, Math.min(COMP_MAX, stepped))
}

/** Whether a stored value is the range shape, for the tolerant read path. */
export function isCompRange(value: unknown): value is CompRange {
  if (typeof value !== 'object' || value === null || Array.isArray(value))
    return false
  const { min, max } = value as Record<string, unknown>
  return (
    typeof min === 'number' &&
    typeof max === 'number' &&
    Number.isFinite(min) &&
    Number.isFinite(max)
  )
}

/**
 * A stored range put in order: junk rejected, both ends on the grid, and the
 * floor never above the ceiling.
 *
 * `undefined` for anything that is not a range, which is the same answer as
 * "nothing said" — an untouched question stores no key at all, so a bag
 * holding junk under one has to read as untouched rather than as a zero.
 *
 * The floor wins a crossed pair, because a crossed pair can only arrive from
 * an edited payload and the floor is the half an applicant states on purpose.
 */
export function settleCompRange(value: unknown): CompRange | undefined {
  if (!isCompRange(value)) return undefined
  const min = snapComp(value.min)
  return { min, max: Math.max(min, snapComp(value.max)) }
}

/**
 * Move one thumb.
 *
 * The two share one track, so each pushes the other rather than crossing it.
 * A floor above the ceiling is not a range anybody means, and letting the
 * thumbs swap roles mid-drag is how a control starts feeling broken.
 */
export function moveComp(
  range: CompRange,
  which: 'min' | 'max',
  value: number,
): CompRange {
  const next = snapComp(value)
  return which === 'min'
    ? { min: next, max: Math.max(next, range.max) }
    : { min: Math.min(next, range.min), max: next }
}

/** A number as money, at the precision the grid actually offers. */
export function compMoney(value: number): string {
  const snapped = snapComp(value)
  if (snapped >= COMP_MAX) return '$1M'
  if (snapped === 0) return '$0'
  return `$${Math.round(snapped / 1000)}k`
}

/** Whether either end is one we say something about. */
export function compFlags(range: CompRange): { low: boolean; high: boolean } {
  return { low: range.min < COMP_LOW, high: range.max > COMP_HIGH }
}

/** The range as one phrase for the admin recap. */
export function compRangeLine(range: CompRange): string {
  return `${compMoney(range.min)} floor, hopeful for ${compMoney(range.max)}`
}

/** Where a dollar amount sits along the track, as a percentage. */
export function compPct(value: number): number {
  return (snapComp(value) / COMP_MAX) * 100
}

/**
 * The least of the track either thumb's pointer zone may shrink to.
 *
 * Two stacked sliders have to divide the track between them or the top one
 * swallows every press. Divided at the midpoint of the two VALUES, two thumbs
 * sitting together near one end leave the other a sliver: a ceiling dragged
 * down to $25k leaves the floor about four pixels of a phone. Nothing on
 * screen says the way out is to move the other thumb first, so an applicant
 * who overshoots one drag is looking at a control that has stopped responding
 * where the thumb visibly is.
 */
export const COMP_MIN_GRAB = 12

/**
 * Where the two thumbs' pointer zones meet, as a percentage of the track.
 *
 * Clamped, so neither zone can shrink past a thumb. Inside a clamped zone a
 * press moves the thumb that does not live there — which is what a range
 * input does anywhere on its track, and is how somebody drags their way back
 * out of the corner they just dragged into.
 */
export function compHitSplit(range: CompRange): number {
  const middle = compPct((range.min + range.max) / 2)
  return Math.min(100 - COMP_MIN_GRAB, Math.max(COMP_MIN_GRAB, middle))
}
